import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useRef } from 'react';
import { z } from 'zod';
import ActionButton from '@/components/Button/ActionButton';
import { AttributeCard } from '@/components/Card/AttributeCard';
import Icon from '@/components/Icon/Icon';
import { HStack, Spacer, VStack } from '@/components/Layout';
import { ProductLogoSmallIDs } from '@/components/Logo';
import { Text } from '@/components/Typography/Text';
import { Confetti, type ConfettiRef } from '@/components/ui/confetti';
import { AvatarPerson } from '@/features/person/components/AvatarPerson';
import { useAuthStore } from '@/lib/auth';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { ManualFormHash } from '../-constants/constant';
const CompletedSchema = z.object({
  addedGroupIdID: z.string(),
  addedGroupIdName: z.string()
});
export const Route = createFileRoute('/_staff/ids/group-id/add/from-scratch/completed')({
  validateSearch: zodValidator(CompletedSchema),
  loaderDeps: ({
    search
  }) => ({
    search
  }),
  component: () => <Completed />
});
function Completed() {
  const confettiRef = useRef<ConfettiRef>(null);
  const {
    session
  } = useAuthStore();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.idManagements.groupId.add.form.result'
  });
  const {
    search
  } = Route.useLoaderDeps();
  const navigate = useNavigate();
  return <VStack className="flex items-center justify-center pt-8">
      <Confetti ref={confettiRef} />
      <AttributeCard title={t('title')} titleStyle="CardHeader" className="w-2/3 p-4" actionButton={<ActionButton variant="secondary" onClick={() => {
      navigate({
        to: '/ids/group-id/$id/detail',
        params: {
          id: search.addedGroupIdID
        }
      });
    }}>
            {t('goToDetail')}
          </ActionButton>}>
        <VStack className="gap-4">
          <HStack className="items-center gap-3">
            <Text variant="h3">{t('addedLabel')}</Text>
            <HStack className="items-center gap-1.5">
              <ProductLogoSmallIDs />
              <Text variant="labelLarge">{search.addedGroupIdName}</Text>
            </HStack>
          </HStack>
          <HStack className="items-center gap-2">
            <Icon iconName="SquareCheckBig" className="text-primary-muted-on-neutral-fill" />
            <Text variant="labelSemiLarge" className="text-primary-muted-on-neutral-fill">
              {t('completedBy')}
            </Text>
            <Spacer />
            <AvatarPerson size={24} name={session?.person?.name ?? undefined} src={session?.person?.avatarUrl ?? undefined} />
            <Text variant="labelLarge">{session?.person?.name ?? '-'}</Text>
          </HStack>
        </VStack>
      </AttributeCard>
      <Spacer variant="l" />
      <ActionButton asChild variant="ghost">
        <Link to="/ids/group-id/add/from-scratch" hash={ManualFormHash.GROUP_ID_INFORMATION} className="flex items-center gap-2">
          <Icon iconName="PlusIcon" size={16} />
          {t('continueAdding')}
        </Link>
      </ActionButton>
    </VStack>;
}